import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Smartphone, Clock, FileText, CheckCircle, User, Bike, Car, ChefHat, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AppOrdersProps, AppOrderCardProps } from './AppOrders.types';
import { OrderStatus } from '../../types/pos.types';

const AppOrderCard: React.FC<AppOrderCardProps> = ({
  order,
  onGenerateReceipt,
  onUpdateStatus
}) => {
  const [showDeliverConfirm, setShowDeliverConfirm] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const getStatusColor = (status: OrderStatus) => {
    switch (status) {
      case 'pendiente': return 'bg-orange-500';
      case 'preparando': return 'bg-blue-500';
      case 'entregado': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusLabel = (status: OrderStatus) => {
    switch (status) {
      case 'pendiente': return 'Pendiente';
      case 'preparando': return 'Preparando';
      case 'entregado': return 'Entregado';
      default: return status;
    }
  };

  const formatCurrency = (amount: number) => {
    return `S/ ${amount.toFixed(2)}`;
  };

  const formatTime = (timeString: string) => {
    return new Date(timeString).toLocaleTimeString('es-PE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDeliveryPlatformIcon = (platform?: string) => {
    switch (platform) {
      case 'rappi': return Bike;
      case 'pedidosya': return Bike;
      default: return null;
    }
  };

  const getDeliveryPlatformLabel = (platform?: string) => {
    switch (platform) {
      case 'rappi': return 'Rappi';
      case 'pedidosya': return 'PedidosYa';
      default: return null;
    }
  };

  const getDeliveryPlatformColor = (platform?: string) => {
    switch (platform) {
      case 'rappi': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'pedidosya': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <Card className="border-l-4 border-l-macartur-primary shadow-lg hover:shadow-xl transition-shadow duration-200">
      <div className="px-6 py-3">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-macartur-primary" />
            <h3 className="text-lg font-bold">#{order.orderNumber}</h3>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`${getStatusColor(order.status)} text-white px-2 py-0.5 text-xs font-semibold`}>
              {getStatusLabel(order.status)}
            </Badge>
            {order.deliveryPlatform && getDeliveryPlatformLabel(order.deliveryPlatform) && (
              <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${getDeliveryPlatformColor(order.deliveryPlatform)}`}>
                {getDeliveryPlatformIcon(order.deliveryPlatform) &&
                  React.createElement(getDeliveryPlatformIcon(order.deliveryPlatform)!, { className: "h-3.5 w-3.5" })
                }
                <span>{getDeliveryPlatformLabel(order.deliveryPlatform)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Info del cliente y hora */}
        <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4" />
            <span className="font-medium">{order.customerName}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            <span>Pedido: {formatTime(order.orderTime)}</span>
          </div>
        </div>

        {/* Layout principal: Productos (izquierda) | Nota + Botón (derecha) */}
        <div className="flex gap-4">
          {/* Items del pedido - Izquierda */}
          <div className="flex-1 bg-gray-50 rounded-lg p-3">
            <h4 className="font-semibold text-sm mb-2">Productos:</h4>
            <div className="space-y-1.5">
              {order.items.map((item, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span className="font-medium">{item.quantity}x {item.product.name}</span>
                  <span className="font-semibold">{formatCurrency(item.subtotal)}</span>
                </div>
              ))}
            </div>
            <div className="border-t pt-2 mt-2 flex justify-between font-bold text-base">
              <span>Total:</span>
              <span className="text-macartur-primary">{formatCurrency(order.totals.total)}</span>
            </div>
          </div>

          {/* Notas y Botón - Derecha */}
          <div className="w-64 flex flex-col gap-3">
            {/* Notas */}
            {order.notes ? (
              <div className="flex-1 bg-yellow-50 border border-yellow-200 rounded-lg p-2.5">
                <p className="text-sm text-yellow-800">
                  <strong>Nota:</strong> {order.notes}
                </p>
              </div>
            ) : (
              <div className="flex-1"></div>
            )}

            {/* Botones */}
            <div className="space-y-2">
              {order.status === 'pendiente' && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      onUpdateStatus(order.id, 'preparando');
                      toast.success(`👨‍🍳 Preparando pedido ${order.orderNumber}`, {
                        description: 'Comanda enviada a cocina',
                        duration: 3000,
                      });
                    }}
                    className="w-full h-12 text-sm font-semibold border-2 border-blue-500 hover:border-blue-600 hover:bg-blue-50 text-blue-700 transition-all duration-200 active:scale-95"
                  >
                    <ChefHat className="h-4 w-4 mr-1" />
                    Preparar
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowRejectConfirm(true)}
                    className="w-full h-12 text-sm font-semibold border-2 border-red-500 hover:border-red-600 hover:bg-red-50 text-red-700 transition-all duration-200 active:scale-95"
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Rechazar
                  </Button>
                </>
              )}
              {order.status === 'preparando' && (
                <Button
                  onClick={() => {
                    onGenerateReceipt(order);
                    onUpdateStatus(order.id, 'entregado');
                    toast.success(`✅ Comprobante emitido`, {
                      description: `Pedido ${order.orderNumber} marcado como entregado`,
                      duration: 3000,
                    });
                  }}
                  className="w-full h-12 text-sm font-semibold bg-macartur-primary hover:bg-macartur-primary/90 transition-all duration-200 active:scale-95"
                >
                  <FileText className="h-4 w-4 mr-1" />
                  Emitir
                </Button>
              )}
              {order.status === 'entregado' && (
                <div className="h-12 flex items-center justify-center text-green-600 font-semibold">
                  <CheckCircle className="h-6 w-6" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog for Delivery */}
      <AlertDialog open={showDeliverConfirm} onOpenChange={setShowDeliverConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Entrega</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro que deseas marcar el pedido <strong>#{order.orderNumber}</strong> como entregado?
              <br />
              <br />
              Cliente: <strong>{order.customerName}</strong>
              <br />
              Total: <strong>{formatCurrency(order.totals.total)}</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onUpdateStatus(order.id, 'entregado');
                toast.success(`🎉 Pedido ${order.orderNumber} entregado`, {
                  description: `Pedido entregado a ${order.customerName}`,
                  duration: 3000,
                });
                setShowDeliverConfirm(false);
              }}
            >
              Confirmar Entrega
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmation Dialog for Reject */}
      <AlertDialog open={showRejectConfirm} onOpenChange={setShowRejectConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rechazar Pedido</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro que deseas rechazar el pedido <strong>#{order.orderNumber}</strong>?
              <br />
              <br />
              Cliente: <strong>{order.customerName}</strong>
              <br />
              Total: <strong>{formatCurrency(order.totals.total)}</strong>
              <br />
              <br />
              <span className="text-red-600 font-medium">Esta acción no se puede deshacer.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                // Aquí puedes agregar lógica adicional para manejar el rechazo
                // Por ahora, simplemente mostramos un toast
                toast.error(`❌ Pedido ${order.orderNumber} rechazado`, {
                  description: `Pedido de ${order.customerName} ha sido rechazado`,
                  duration: 3000,
                });
                setShowRejectConfirm(false);
                // Opcional: Puedes agregar un nuevo estado o eliminar el pedido de la lista
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Confirmar Rechazo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export const AppOrders: React.FC<AppOrdersProps> = ({
  orders,
  onGenerateReceipt,
  onUpdateStatus,
  fullWidth = false
}) => {
  const [filter, setFilter] = useState<OrderStatus | 'all'>('all');
  const [platformFilter, setPlatformFilter] = useState<string | 'all'>('all');
  
  const filteredOrders = orders.filter(order => {
    const statusMatch = filter === 'all' || order.status === filter;
    const platformMatch = platformFilter === 'all' || order.deliveryPlatform === platformFilter;
    return statusMatch && platformMatch;
  });

  const orderCounts = {
    pendiente: orders.filter(o => o.status === 'pendiente').length,
    preparando: orders.filter(o => o.status === 'preparando').length,
    entregado: orders.filter(o => o.status === 'entregado').length,
  };

  const platformCounts = {
    rappi: orders.filter(o => o.deliveryPlatform === 'rappi').length,
    pedidosya: orders.filter(o => o.deliveryPlatform === 'pedidosya').length,
  };

  return (
    <div className={`flex flex-col bg-white h-full ${!fullWidth ? 'border-l' : ''}`}>
      <div className="p-4 border-b bg-gray-50 flex-shrink-0">
        <div className="flex items-center gap-2 mb-3">
          <Smartphone className="h-5 w-5 text-macartur-primary" />
          <h2 className="text-lg font-semibold">Pedidos App</h2>
        </div>
        
        {/* Filtros combinados - Optimizados para touch */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant={filter === 'all' && platformFilter === 'all' ? 'default' : 'outline'}
            onClick={() => {
              setFilter('all');
              setPlatformFilter('all');
            }}
            className="h-10 px-4 text-sm font-medium transition-all duration-200 active:scale-95"
          >
            Todos ({orders.length})
          </Button>
          <Button
            variant={filter === 'pendiente' ? 'default' : 'outline'}
            onClick={() => setFilter('pendiente')}
            className="h-10 px-4 text-sm font-medium transition-all duration-200 active:scale-95"
          >
            Pendientes ({orderCounts.pendiente})
          </Button>
          <Button
            variant={filter === 'preparando' ? 'default' : 'outline'}
            onClick={() => setFilter('preparando')}
            className="h-10 px-4 text-sm font-medium transition-all duration-200 active:scale-95"
          >
            Preparando ({orderCounts.preparando})
          </Button>
          <Button
            variant={platformFilter === 'rappi' ? 'default' : 'outline'}
            onClick={() => {
              setPlatformFilter('rappi');
              if (filter === 'all') setFilter('all');
            }}
            className={`h-10 px-4 text-sm font-medium transition-all duration-200 active:scale-95 ${
              platformFilter === 'rappi'
                ? 'bg-orange-600 hover:bg-orange-700 text-white'
                : 'bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-200'
            }`}
          >
            <Bike className="h-4 w-4 mr-2" />
            Rappi ({platformCounts.rappi})
          </Button>
          <Button
            variant={platformFilter === 'pedidosya' ? 'default' : 'outline'}
            onClick={() => {
              setPlatformFilter('pedidosya');
              if (filter === 'all') setFilter('all');
            }}
            className={`h-10 px-4 text-sm font-medium transition-all duration-200 active:scale-95 ${
              platformFilter === 'pedidosya'
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-red-50 hover:bg-red-100 text-red-700 border-red-200'
            }`}
          >
            <Bike className="h-4 w-4 mr-2" />
            PedidosYa ({platformCounts.pedidosya})
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 min-h-0">
        {filteredOrders.length === 0 ? (
          <div className="text-center text-gray-500 mt-8">
            <Smartphone className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No hay pedidos
              {filter !== 'all' && ` en estado "${filter}"`}
              {platformFilter !== 'all' && ` de ${getDeliveryPlatformLabel(platformFilter)}`}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredOrders.map((order) => (
              <AppOrderCard
                key={order.id}
                order={order}
                onGenerateReceipt={onGenerateReceipt}
                onUpdateStatus={onUpdateStatus}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

function getDeliveryPlatformLabel(platform: string): string {
  switch (platform) {
    case 'rappi': return 'Rappi';
    case 'pedidosya': return 'PedidosYa';
    default: return platform;
  }
}